import React, { useState } from 'react'
import classNames from 'classnames'
import { HlsAirPlayButton } from './HlsAirPlayButton'
import { HlsAudioButton } from './HlsAudioButton'
import { HlsCastButton } from './HlsCastButton'
import { HlsFullscreenButton } from './HlsFullscreenButton'
import { HlsParentalControlsButton } from './HlsParentalControlsButton'
import { HlsPipButton } from './HlsPipButton'
import { HlsPlayPauseButton } from './HlsPlayPauseButton'
import { useHlsPlayerUI } from './HlsPlayerUIContext'
import { HlsSeekBar } from './HlsSeekBar'
import { HlsSettingsButton } from './HlsSettingsButton'
import { HlsShareButton } from './HlsShareButton'
import { HlsSubtitleButton } from './HlsSubtitleButton'
import { HlsTimeDisplay } from './HlsTimeDisplay'
import { HlsVolumeControl } from './HlsVolumeControl'

type SettingsPage = 'quality' | 'audio' | 'subtitles' | 'speed'

type CastState = 'unavailable' | 'available' | 'connecting' | 'connected'
type AirPlayState = 'unavailable' | 'available' | 'connected'

interface HlsControlBarProps {
  visible: boolean
  onInteraction: () => void
  showContentShareButton?: boolean
  onShare?: () => void
  showParentalControlsButton?: boolean
  parentalControlsEnabled?: boolean
  parentalControlsApplied?: number
  parentalControlsTotal?: number
  onOpenParentalControls?: () => void
  subtitleTrackCount?: number
  audioTrackCount?: number
  onSettingsOpenChange?: (isOpen: boolean) => void
  castState?: CastState
  onStartCast?: () => void
  onStopCast?: () => void
  airPlayState?: AirPlayState
  onShowAirPlayPicker?: () => void
}

/**
 * Bottom control bar for the HLS.js player.
 * Contains seek bar, play/pause, volume, and time display.
 * Fades in/out based on the `visible` prop.
 *
 * Layout (top to bottom):
 * - Seek bar (full width)
 * - Controls row: play/pause | volume + slider | time | ... spacer ... | share | parental | audio | subtitles | settings | PiP | fullscreen
 */
export const HlsControlBar: React.FC<HlsControlBarProps> = ({
  visible,
  onInteraction,
  showContentShareButton,
  onShare,
  showParentalControlsButton,
  parentalControlsEnabled = false,
  parentalControlsApplied = 0,
  parentalControlsTotal = 0,
  onOpenParentalControls,
  subtitleTrackCount = 0,
  audioTrackCount = 0,
  onSettingsOpenChange,
  castState = 'unavailable',
  onStartCast,
  onStopCast,
  airPlayState = 'unavailable',
  onShowAirPlayPicker,
}) => {
  const { videoRef } = useHlsPlayerUI()
  const [requestedPage, setRequestedPage] = useState<SettingsPage | null>(null)

  const showSubtitleButton = subtitleTrackCount >= 2
  const showAudioButton = audioTrackCount >= 2

  return (
    <div
      data-hls-control-bar
      className={classNames(
        'absolute inset-x-0 bottom-0 flex flex-col bg-gradient-to-t from-black/70 via-black/40 to-transparent px-3 pb-1 pt-1 transition-opacity duration-[400ms] sm:pt-10 sm:pb-2 ',
        visible ? 'opacity-100' : 'pointer-events-none opacity-0',
      )}
      onClick={onInteraction}
    >
      {/* Seek bar */}
      <HlsSeekBar />

      {/* Controls row */}
      <div className="flex items-center">
        {/* Left controls */}
        <div className="flex items-center gap-1.5">
          <HlsPlayPauseButton />
          <HlsVolumeControl />
        </div>
        <div className="ml-4 hidden sm:block">
          <HlsTimeDisplay />
        </div>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right controls — tight spacing matching Bitmovin (1px margin per side) */}
        <div className="flex items-center gap-x-1">
          {showContentShareButton && onShare && <HlsShareButton onShare={onShare} />}
          {showParentalControlsButton && onOpenParentalControls && (
            <HlsParentalControlsButton
              enabled={parentalControlsEnabled}
              applied={parentalControlsApplied}
              total={parentalControlsTotal}
              onOpenModal={onOpenParentalControls}
            />
          )}
          {showAudioButton && <HlsAudioButton onOpenAudio={() => setRequestedPage('audio')} />}
          {showSubtitleButton && <HlsSubtitleButton onOpenSubtitles={() => setRequestedPage('subtitles')} />}
          <HlsSettingsButton
            requestedPage={requestedPage}
            onRequestedPageHandled={() => setRequestedPage(null)}
            onOpenChange={onSettingsOpenChange}
          />
          <HlsPipButton videoRef={videoRef} />
          {onShowAirPlayPicker && <HlsAirPlayButton airPlayState={airPlayState} onShowPicker={onShowAirPlayPicker} />}
          {onStartCast && onStopCast && (
            <HlsCastButton castState={castState} onStartCast={onStartCast} onStopCast={onStopCast} />
          )}
          <HlsFullscreenButton />
        </div>
      </div>
    </div>
  )
}
